/**
 * One undo stack for the whole app (audit #14: nothing could be undone, and the
 * file, ink and colourway × deleted instantly).
 *
 * An entry is a label and a closure that puts the world back — never a snapshot
 * of the whole store. A snapshot would also undo everything that landed in
 * between (a run finishing, another document's inspection), and the things a
 * shop actually wants back are small and local: this colour, this file, this
 * width. Each setter that pushes an entry therefore captures exactly the slice
 * it is about to overwrite.
 *
 * Deliberately module state rather than a store field: the entries are
 * closures, nothing renders from them, and a store field would put a hundred
 * captured job objects through every persistence subscription in the app.
 */

/** A push carrying the same key as the one before it, this soon after it, is the
 *  same gesture: a slider dragged across ten values, a colour typed a character
 *  at a time. It replaces that entry instead of stacking a tenth of an inch of
 *  history the user would have to press Ctrl+Z ten times to get through. */
export const UNDO_COALESCE_MS = 500;
/** How many steps back the app remembers. Fifty is far more than a shop reaches
 *  for and small enough that the captured jobs and ink lists cost nothing. */
export const UNDO_MAX = 50;

/**
 * Which entry a caller is holding on to. A toast raised by a removal keeps the
 * token of the entry it names, so its Undo button reverses THAT change however
 * many edits have landed in the six seconds it is on screen — the button's own
 * words are the promise, and "whatever is on top of the stack" does not keep it.
 *
 * Opaque on purpose: it is an identity, not a position, and a coalesced push
 * hands back the token of the entry it merged into.
 */
export type UndoToken = number;

/**
 * WHERE an entry belongs: the document it is about, and the mode whose runner has
 * to react to its restore.
 *
 * Undo is a writer like any other, and the three runners only reconcile the mode
 * they are in against the document on screen. So an entry is only offered where
 * putting it back is visible AND re-rendered: a job restored in DTF mode is a
 * store write no subscription acts on (the preview, the footer and the Details
 * rows stay the last render's), and an ink list restored outside screen print
 * leaves the plates, the checks and the films export belonging to the build
 * before it. Restored in its own mode, on its own document, the existing
 * subscriptions see the change and do the work — which is also what makes the
 * toast's "Undid Width" true of something the shop can see.
 *
 * Either field may be absent, which means "anywhere": a closed FILE is not an
 * edit to a document's state, it is a change to the list every mode shows, and
 * Ctrl+Z has to bring it back from wherever the shop happens to be standing.
 */
export interface UndoScope {
  docId?: string | null;
  mode?: string;
}

interface Entry {
  token: UndoToken;
  label: string;
  restore: () => void;
  /** What makes two pushes the same gesture; absent for a one-off (a close, a delete). */
  key?: string;
  /** When the entry was last pushed onto, for the coalescing window. */
  at: number;
  /** The document this entry is about; absent for an app-wide one (a file close). */
  docId?: string;
  /** The mode whose runner reacts to the restore; absent for an app-wide one. */
  mode?: string;
}

/**
 * Is this entry one the caller may take? An entry that named no document or no
 * mode is in scope everywhere; one that named either is offered only there.
 * A caller passing no scope at all (a test, `flushClosedDocs`) takes anything.
 */
function inScope(e: Entry, scope: UndoScope | undefined): boolean {
  if (!scope) return true;
  if (e.docId !== undefined && e.docId !== scope.docId) return false;
  if (e.mode !== undefined && e.mode !== scope.mode) return false;
  return true;
}

let stack: Entry[] = [];
let nextToken = 0;
/** While a group is open, every push lands here instead of on the stack. */
let group: Entry[] | null = null;

/**
 * Collapse everything `fn` pushes into ONE undo step, labelled `label`.
 *
 * One thing a shop does is often several store writes: removing a colourway
 * rewrites the colour list AND the job (a different blank wears the colour that
 * is now on screen). Without this, Ctrl+Z would put the garment back and leave
 * the colour gone, and the second press would be needed to finish a single
 * mistake. The captured restores are run in reverse, so the world is rebuilt in
 * the order it was taken apart.
 *
 * Nested calls join the outer group rather than starting one of their own, and a
 * `fn` that pushes nothing leaves no entry at all.
 */
export function undoGroup<T>(label: string, fn: () => T, key?: string, scope?: UndoScope): T {
  if (group) return fn();
  group = [];
  try {
    return fn();
  } finally {
    const captured = group;
    group = null;
    if (captured.length) {
      // The SCOPE is the group's, not the captured entries': they are the parts of
      // one action and never reach the stack on their own.
      pushUndo(label, () => { for (let i = captured.length - 1; i >= 0; i--) captured[i].restore(); }, key, scope);
    }
  }
}

/**
 * Remember how to undo what is about to happen. `restore` is called with no
 * arguments and must not throw; it is written by the setter that is making the
 * change, from the values it is about to replace.
 *
 * With a `key`, a push that arrives within `UNDO_COALESCE_MS` of the previous
 * push of the SAME key replaces its label but KEEPS its restore closure — so a
 * slider dragged across a dozen values is one undo, back to where the drag
 * started, not a dozen undos back through the drag.
 */
export function pushUndo(label: string, restore: () => void, key?: string, scope?: UndoScope): UndoToken {
  const now = Date.now();
  const token = ++nextToken;
  // Inside a group the entries are collected, never coalesced: they are the parts
  // of one action, and the group's own push is what coalesces with its neighbours.
  if (group) { group.push({ token, label, restore, key, at: now }); return token; }
  const top = stack[stack.length - 1];
  if (key !== undefined && top && top.key === key && now - top.at <= UNDO_COALESCE_MS) {
    // The label is the new one (it names what the user last did); the restore is
    // the OLD one (it is the only snapshot that predates the whole gesture). The
    // timestamp moves so a gesture that keeps going keeps coalescing. The token
    // is the ENTRY's, so a toast raised on the first write of a gesture still
    // points at the entry the rest of it merged into.
    top.label = label;
    top.at = now;
    return top.token;
  }
  stack.push({ token, label, restore, key, at: now, docId: scope?.docId ?? undefined, mode: scope?.mode });
  if (stack.length > UNDO_MAX) stack = stack.slice(stack.length - UNDO_MAX);
  return token;
}

/**
 * Undo the most recent change IN SCOPE and return its label, or null when there is
 * nothing there to undo.
 *
 * The newest entry the scope allows, not the newest entry: an edit made to another
 * document, or in another mode, stays on the stack for the shop to reach by going
 * back to where they made it. Ctrl+Z therefore only ever moves something the shop
 * is looking at — and the entries it skips are not lost, they are just not here.
 */
export function undo(scope?: UndoScope): string | null {
  for (let i = stack.length - 1; i >= 0; i--) {
    if (!inScope(stack[i], scope)) continue;
    const [entry] = stack.splice(i, 1);
    entry.restore();
    return entry.label;
  }
  return null;
}

/** Is there anything Ctrl+Z would take in this scope? What tells "nothing to undo"
 *  apart from "nothing to undo HERE". */
export function canUndo(scope?: UndoScope): boolean {
  return stack.some((e) => inScope(e, scope));
}

/**
 * Undo back to and including the entry a caller is holding a token for, and
 * return its label — or null when that entry is no longer on the stack (it has
 * been undone already, or dropped with its document, or pushed off the end).
 *
 * Everything ABOVE it that is in the same scope goes too, newest first. The
 * alternative — reaching into the middle of the stack — would put a snapshot taken
 * before those edits back over them, which is how an undo loses work nobody asked
 * it to touch. So the toast's Undo keeps its promise ("Charcoal is back") and takes
 * the edits made in the six seconds since with it, exactly as pressing Ctrl+Z that
 * many times would.
 *
 * An entry OUTSIDE the scope is refused (null), for the same reason Ctrl+Z will not
 * take it: pressed after a mode switch, the toast's button would restore something
 * the mode's renderer never reconciles, so it says the change can no longer be
 * undone instead. Entries above it belonging to another document or another mode are
 * neither undone nor dropped — they are not this button's business.
 */
export function undoTo(token: UndoToken, scope?: UndoScope): string | null {
  const i = stack.findIndex((e) => e.token === token);
  if (i < 0 || !inScope(stack[i], scope)) return null;
  const label = stack[i].label;
  // Everything above it that is in the same scope, newest first; entries belonging
  // to another document or another mode stay exactly where they are, in order.
  const take: Entry[] = [], keep: Entry[] = [];
  for (let k = stack.length - 1; k >= i; k--) (inScope(stack[k], scope) ? take : keep).push(stack[k]);
  stack = [...stack.slice(0, i), ...keep.reverse()];
  for (const e of take) e.restore();
  return label;
}

/** The token of the change Ctrl+Z would undo — what a caller raising a toast for
 *  the action it has just performed passes to `showToast`. */
export function topUndoToken(): UndoToken | null {
  return stack.length ? stack[stack.length - 1].token : null;
}

/** The label of the change Ctrl+Z would undo, without undoing it. */
export function undoLabel(): string | null {
  return stack.length ? stack[stack.length - 1].label : null;
}

/** How many steps back the stack holds. For tests and for a disabled Undo button. */
export function undoDepth(): number {
  return stack.length;
}

/**
 * Drop every entry under a key, for a change that can no longer be undone — a
 * closed document whose pixels have been freed, say. Without this, Ctrl+Z would
 * report a document restored and hand back an entry with no bitmaps behind it.
 */
export function forgetUndo(key: string): void {
  stack = stack.filter((e) => e.key !== key);
}

/**
 * The same, for every entry whose key matches — how a dropped document takes ALL
 * of its history with it, not just the close itself. A placement or an ink list
 * belonging to a document nobody can open any more restores into nothing, and an
 * undo that silently does nothing while the app says "Undid Width" is worse than
 * one that is not offered.
 */
export function forgetUndoWhere(match: (key: string) => boolean): void {
  stack = stack.filter((e) => e.key === undefined || !match(e.key));
}

/**
 * What the app says once a change has been undone. A removal reads as the thing
 * coming back ("Charcoal is back") rather than as a sentence about the undo
 * ("Undid Removed Charcoal"), which is the plain-words half of the same job the
 * labels themselves do.
 */
export function undoneMessage(label: string): string {
  const back = /^(?:Removed|Closed) (.+)$/.exec(label);
  return back ? `${back[1]} is back` : `Undid ${label}`;
}

/** Forget everything. Used by tests; nothing in the app clears the stack. */
export function clearUndo(): void {
  stack = [];
}

/** The keys Ctrl+Z (Cmd+Z on a Mac) arrives as. Shift+Ctrl+Z is redo elsewhere
 *  and Alt+Ctrl+Z is nothing here, so neither counts. */
export function isUndoShortcut(e: { key: string; ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean; altKey?: boolean }): boolean {
  if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey) return false;
  return e.key === 'z' || e.key === 'Z';
}

/** What an open modal dialog looks like in the DOM. One selector, so the keyboard
 *  handler and its test cannot disagree about what counts as one. */
export const MODAL_DIALOG_SELECTOR = '[role="dialog"][aria-modal="true"]';

/**
 * Is a modal dialog up — or did this key press arrive on something inside one?
 *
 * Ctrl+Z belongs to the dialog while one is open, and the dialog (the template
 * editor) has no undo of its own: the key must do NOTHING rather than reverse a
 * placement, a colourway or a closed file in the app behind the 60 % backdrop,
 * where the shop cannot see what it did and cannot see the toast that named it.
 *
 * Both halves are checked because either can be true on its own: focus may sit on
 * a control inside the dialog (`closest`), or on `<body>` with the dialog still up
 * (`querySelector`) — which is what a click on the backdrop leaves behind.
 */
export function inModalDialog(
  el: { closest?(s: string): unknown } | null | undefined,
  root?: { querySelector(s: string): unknown } | null,
): boolean {
  if (el?.closest?.(MODAL_DIALOG_SELECTOR)) return true;
  return !!root?.querySelector(MODAL_DIALOG_SELECTOR);
}

/** The input types Ctrl+Z belongs to the FIELD in, not to the app: the browser's
 *  own text undo is the only thing that can put a half-typed word back. */
const TEXT_INPUT_TYPES = new Set(['text', 'number', 'search', 'email', 'tel', 'url', 'password', 'date', 'time', 'month', 'week', 'datetime-local']);

/**
 * Is this element somewhere a person types? A checkbox, a range and a colour chip
 * are not — Ctrl+Z with one of those focused is still the app's undo, which is
 * exactly the case a shop hits after clicking a swatch or a toggle.
 */
export function isTextEntry(el: { tagName?: string; type?: string; isContentEditable?: boolean } | null | undefined): boolean {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = (el.tagName ?? '').toLowerCase();
  if (tag === 'textarea' || tag === 'select') return true;
  return tag === 'input' && TEXT_INPUT_TYPES.has((el.type ?? 'text').toLowerCase());
}
